import type { CanvasClass } from '../types/creative-template.types';
import type {
  BreakpointSpec,
  ComplianceSpec,
  CreativeTemplateWritePayload,
  FallbackSpec,
  TemplateConstraint,
  TemplateGenerationPolicy,
  TemplateRoleSpec,
  TemplateZone,
} from '../types/creative-template.types';

export const EMPTY_CONSTRAINT: TemplateConstraint = {
  kind: 'must_appear',
  role: 'logo',
};

export const EMPTY_ROLE_SPEC: TemplateRoleSpec = {
  role: 'logo',
  required: true,
  min_readiness: 'extensible',
  constraints: [],
  drop_rank: null,
};

export const EMPTY_ZONE: TemplateZone = {
  x: 0,
  y: 0,
  w: 0.2,
  h: 0.2,
};

export const EMPTY_BREAKPOINT: BreakpointSpec = {
  canvas_class: 'panel_square',
  zones: {},
  max_text_roles: 3,
  min_hero_area_frac: 0.2,
};

export const DEFAULT_FALLBACK: FallbackSpec = {
  drop_roles: ['hero', 'accent'],
  promote_roles: ['headline'],
  background: 'brand_solid',
};

export const DEFAULT_GENERATION_POLICY: TemplateGenerationPolicy = {
  level: 'background_only',
  max_generated_area_frac: 0.55,
  require_provenance: true,
};

export const DEFAULT_COMPLIANCE: ComplianceSpec = {
  requires_disclaimer: false,
  min_legal_font_px: 11,
  disclaimer_role: 'legal',
};

export const DEFAULT_ART_DIRECTION = {
  type_pairing: null,
  palette_strategy: 'brand_exact' as const,
};

export const CANVAS_ASPECT_RATIOS: Record<CanvasClass, number> = {
  ribbon: 8.09,
  skyscraper: 0.2,
  panel_wide: 1.78,
  panel_square: 1,
  panel_tall: 0.56,
};

const ZONE_COLOURS: Record<string, string> = {
  background: '#E5E7EB',
  hero: '#93C5FD',
  logo: '#FDE68A',
  headline: '#FCA5A5',
  body: '#C4B5FD',
  cta: '#86EFAC',
  legal: '#D1D5DB',
  accent: '#FDBA74',
};

export function zoneColour(role: string): string {
  return ZONE_COLOURS[role] ?? '#E5E7EB';
}

function trimCoerced(value: unknown): string {
  return String(value ?? '').trim();
}

export function trimStringList(values: string[]): string[] {
  return values.map((value) => trimCoerced(value)).filter(Boolean);
}

export function buildCreativeTemplatePayload(
  values: CreativeTemplateWritePayload,
): CreativeTemplateWritePayload {
  return {
    ...values,
    template_id: values.template_id?.trim(),
    name: values.name?.trim(),
    change_note: values.change_note?.trim(),
    fallback: values.fallback
      ? {
          ...values.fallback,
          drop_roles: trimStringList(values.fallback.drop_roles ?? []),
          promote_roles: trimStringList(values.fallback.promote_roles ?? []),
        }
      : undefined,
  };
}

export function validateCreativeTemplateForm(
  values: CreativeTemplateWritePayload,
  options: { isCreate: boolean },
): string | null {
  if (!values.name?.trim()) {
    return 'Name is required.';
  }
  if (options.isCreate && !values.template_id?.trim()) {
    return 'Template ID is required when creating a template.';
  }
  if (!values.roles?.length) {
    return 'At least one role spec is required.';
  }
  if (!values.breakpoints?.length) {
    return 'At least one breakpoint is required.';
  }
  return null;
}

export function parseCreativeTemplateMutationError(error: unknown): string {
  if (!error || typeof error !== 'object' || !('data' in error)) {
    return 'Could not save creative template. You may need staff or admin access.';
  }
  const data = (error as { data?: Record<string, unknown> }).data;
  if (!data) {
    return 'Could not save creative template.';
  }
  if (typeof data.detail === 'string') {
    return data.detail;
  }
  const messages = Object.entries(data)
    .map(([field, value]) => {
      if (Array.isArray(value)) {
        return `${field}: ${value.join(', ')}`;
      }
      return `${field}: ${String(value)}`;
    });
  return messages.length > 0 ? messages.join(' ') : 'Could not save creative template.';
}
